import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@folio/components';
import {
  ConformityAcceptAllControl,
  type DocumentConformityState,
} from '../DocumentsListsPolicyModule/DocumentsListsPolicyModule';
import { DocumentViewer } from '@folio/components/features/document-viewer/DocumentViewer';
import { useDocumentViewer } from '@folio/components/features/document-viewer/useDocumentViewer';
import {
  DocumentProcessingStatus,
  isDocumentProcessing,
} from '@folio/components/features/document-viewer/DocumentProcessingStatus';
import { ExtractionStatusBadge } from '@folio/components/features/document-extraction';
import type {
  DocumentViewerDocument,
  DocumentCustomCategorieMapping,
} from '@folio/components/features/document-viewer/types';
import { DocumentsCollection } from '@folio/components/module-elements/Documents/DocumentsCollection';
import { useAssertedParams } from '@folio/lib';
import { useDocumentUploadMode } from '@folio/utils/useDocumentUploadMode';
import { useQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerListDocumentsQueryOptions,
  brokerageFirmDocumentExtractionControllerGetBrokerageFirmExtractionsQueryOptions,
  brokerageFirmDocumentValidityControllerGetBrokerageFirmValidityQueryOptions,
  documentCoherenceControllerGetDocumentCoherenceQueryOptions,
  configControllerGetPublicTenantConfigQueryOptions,
  useDocumentExtractionControllerRetryDocumentExtraction,
  useDocumentExtractionControllerSubmitExtractionFeedback,
  useBrokerageFirmDocumentValidityControllerOverrideDocumentValidity,
  keyValueFieldsToObject,
  type GetDocumentExtractionOutput,
  type SubmitFeedbackBody,
} from 'korint-sdk';
import { useFeatureFlag, type AIValidityResult } from 'korint-sdk/react';
import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@folio/components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import {
  getDocumentCategoryLabel,
  extractBrokerageFirmDocumentConfigs,
  translateBrokerageFirmDocument,
} from '@folio/utils/getDocumentCategoryLabel';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { Center, Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type DocumentsListBrokerageFirmModuleProps = {
  /**
   * The documents list.
   */
  documentsList: Array<{
    category: string;
    allowMultiple?: boolean;
    required?: boolean;
  }>;

  /**
   * The title of the module.
   */
  title: string;

  /**
   * The firm whose documents are listed. Defaults to the `brokerageFirmId` route param, which
   * pages reached without one (a broker's own firm) do not have.
   */
  brokerageFirmId?: string;

  /**
   * Optional document custom category mappings for batch upload mode.
   * These provide labels for document categories when classifying documents.
   */
  documentCustomCategorieMappings?: DocumentCustomCategorieMapping[];

  /**
   * Product ID for extraction feature gate. When not provided, extraction is disabled.
   */
  productId?: string;

  conformityByDocumentId?: Map<string, DocumentConformityState>;
  onAcceptDocumentConformity?: (documentId: string) => void;
  onRejectDocumentConformity?: (documentId: string, reason: string) => void;
  onCancelDocumentConformity?: (documentId: string) => void;
  onAcceptAllDocumentConformity?: () => void;
};

export function DocumentsListBrokerageFirmModule({
  documentsList,
  title,
  brokerageFirmId: brokerageFirmIdOverride,
  documentCustomCategorieMappings,
  productId,
  conformityByDocumentId,
  onAcceptDocumentConformity,
  onRejectDocumentConformity,
  onCancelDocumentConformity,
  onAcceptAllDocumentConformity,
}: DocumentsListBrokerageFirmModuleProps) {
  const { t } = useTranslation();

  const { brokerageFirmId: brokerageFirmIdFromRoute } = useAssertedParams();
  const brokerageFirmId = brokerageFirmIdOverride ?? brokerageFirmIdFromRoute;

  const uploadMode = useDocumentUploadMode('brokerageFirmDocuments');
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    data: documents,
    isLoading,
    isError,
    isSuccess,
  } = useQuery({
    ...brokerageFirmControllerListDocumentsQueryOptions(brokerageFirmId, {
      categories: documentsList.map(({ category }) => category),
      pageInfo: { count: 100 },
    }),
    select: data => {
      const docs = data?.data.map(document => ({
        ...document,
        metadata: keyValueFieldsToObject(document.metadata),
      }));
      return docs;
    },
  });

  const { data: tenantConfig } = useQuery({
    ...configControllerGetPublicTenantConfigQueryOptions(),
  });

  const brokerageFirmDocumentConfigs = extractBrokerageFirmDocumentConfigs(tenantConfig);

  const extractionEnabled = useFeatureFlag('perm_ai_extraction', productId);
  const complianceEnabled = useFeatureFlag('perm_ai_validity', productId);
  const coherenceEnabled = useFeatureFlag('ops_ai_coherence');
  const { data: coherence } = useQuery({
    ...documentCoherenceControllerGetDocumentCoherenceQueryOptions(brokerageFirmId),
    enabled: coherenceEnabled,
    retry: false,
    refetchInterval: query =>
      query.state.data?.documents.some(({ status }) => status === 'PENDING') ? 2000 : false,
  });

  const { data: extractions } = useQuery({
    ...brokerageFirmDocumentExtractionControllerGetBrokerageFirmExtractionsQueryOptions(
      brokerageFirmId,
    ),
    enabled: extractionEnabled,
    refetchInterval: query => {
      const data = query.state.data;
      if (!data?.extractions) return false;
      const hasPending = data.extractions.some(e => e.status === 'PENDING');
      return hasPending ? 2000 : false;
    },
  });

  const { data: validities } = useQuery({
    ...brokerageFirmDocumentValidityControllerGetBrokerageFirmValidityQueryOptions(brokerageFirmId),
    enabled: complianceEnabled,
    refetchInterval: query => {
      const data = query.state.data;
      if (!data?.validities) return false;
      const hasPending = data.validities.some(c => c.status === 'PENDING');
      return hasPending ? 2000 : false;
    },
  });

  const validityQueryKey =
    brokerageFirmDocumentValidityControllerGetBrokerageFirmValidityQueryOptions(
      brokerageFirmId,
    ).queryKey;

  const { can } = useUserPermissions({
    productId,
    actions: ['OVERRIDE_DOCUMENT_VALIDITY'],
  });
  const canOverrideValidity = can.OVERRIDE_DOCUMENT_VALIDITY;

  const { mutate: overrideValidity, isPending: isValidityOverriding } =
    useBrokerageFirmDocumentValidityControllerOverrideDocumentValidity({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: validityQueryKey });
          toast({ description: t('documents.validity.overrideSuccess') });
        },
        onError: () => {
          toast({
            description: t('documents.validity.overrideError'),
            variant: 'destructive',
          });
        },
      },
    });

  const handleValidityOverride = useCallback(
    (documentId: string, newStatus: 'VALID' | 'INVALID', reason?: string) => {
      overrideValidity({ brokerageFirmId, documentId, data: { status: newStatus, reason } });
    },
    [overrideValidity, brokerageFirmId],
  );

  const getExtractionForDocument = useCallback(
    (documentId: string): GetDocumentExtractionOutput | undefined => {
      return extractions?.extractions?.find(e => e.documentId === documentId);
    },
    [extractions],
  );

  const validityByDocumentId = useMemo(
    () =>
      new Map(
        (validities?.validities ?? [])
          .filter(c => c.status !== 'PENDING')
          .map(c => [c.documentId, c as AIValidityResult]),
      ),
    [validities],
  );

  const { mutate: retryExtraction, isPending: isRetryingExtraction } =
    useDocumentExtractionControllerRetryDocumentExtraction({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            predicate: query => {
              const key = query.queryKey[0];
              return (
                typeof key === 'object' &&
                key !== null &&
                'url' in key &&
                (key as { url: string }).url ===
                  '/brokerage-firms/:brokerageFirmId/document-extractions'
              );
            },
          });
          toast({ description: t('documents.extraction.retrySuccess') });
        },
      },
    });

  const { mutate: submitFeedback, isPending: isFeedbackPending } =
    useDocumentExtractionControllerSubmitExtractionFeedback({
      mutation: {
        onSuccess: () => {
          toast({ description: t('documents.extraction.feedback.submitted') });
        },
      },
    });

  const handleRetryExtraction = useCallback(
    (documentId: string) => {
      retryExtraction({ documentId });
    },
    [retryExtraction],
  );

  const sortedDocumentsList = useMemo(
    () => [...documentsList].sort((a, b) => (b.required ? 1 : 0) - (a.required ? 1 : 0)),
    [documentsList],
  );

  const viewableDocuments: DocumentViewerDocument[] = useMemo(() => {
    if (!documents) return [];

    return documents
      .filter(doc => doc.signedUrl)
      .map(doc => ({
        id: doc.id,
        name: doc.name,
        signedUrl: doc.signedUrl || '',
        createdAt: doc.createdAt,
        type: doc.type,
        category: doc.category,
        metadata: doc.metadata as Record<string, string | number | boolean | undefined>,
      }));
  }, [documents]);

  const { selectedDocument, selectDocument, closeViewer } = useDocumentViewer(viewableDocuments);

  const selectedDocumentExtraction = selectedDocument
    ? getExtractionForDocument(selectedDocument.id)
    : undefined;

  const selectedDocumentValidity = selectedDocument
    ? validityByDocumentId.get(selectedDocument.id)
    : undefined;

  const selectedDocumentCoherence = coherenceEnabled
    ? coherence?.documents.find(({ documentId }) => documentId === selectedDocument?.id)
    : undefined;

  const handleExtractionFeedback = useCallback(
    (feedback: SubmitFeedbackBody) => {
      if (!selectedDocument) return;
      submitFeedback({ documentId: selectedDocument.id, data: feedback });
    },
    [selectedDocument, submitFeedback],
  );

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row justify-between items-start space-y-0">
          <CardTitle>{title}</CardTitle>
          {conformityByDocumentId && (
            <Group>
              <ConformityAcceptAllControl
                conformityByDocumentId={conformityByDocumentId}
                onAcceptAll={onAcceptAllDocumentConformity}
              />
            </Group>
          )}
        </CardHeader>
        <CardContent>
          <Stack gap={1}>
            {isLoading && (
              <Stack gap={2}>
                <Skeleton className="h-[40px] w-full" />
                <Skeleton className="h-[40px] w-full" />
                <Skeleton className="h-[40px] w-full" />
                <Skeleton className="h-[40px] w-full" />
                <Skeleton className="h-[40px] w-full" />
                <Skeleton className="h-[40px] w-full" />
              </Stack>
            )}
            {isError && <Center className="h-full">{t('documentsListsPolicy.fetchError')}</Center>}
            {isSuccess && (
              <DocumentsCollection
                groups={sortedDocumentsList.map(({ category, allowMultiple = false }) => {
                  const matchingDocuments = documents.filter(
                    document => document.category === category,
                  );
                  return {
                    id: category,
                    label: getDocumentCategoryLabel(
                      category,
                      brokerageFirmDocumentConfigs,
                      translateBrokerageFirmDocument(t),
                    ),
                    documents: allowMultiple ? matchingDocuments : matchingDocuments.slice(0, 1),
                  };
                })}
                onView={document => {
                  const viewableDocument = viewableDocuments.find(({ id }) => id === document.id);
                  if (viewableDocument) selectDocument(viewableDocument);
                }}
                renderBadges={document => {
                  const extraction = extractionEnabled
                    ? getExtractionForDocument(document.id)
                    : undefined;
                  const validity = validities?.validities.find(
                    ({ documentId }) => documentId === document.id,
                  );
                  const isProcessing = isDocumentProcessing({
                    extractionStatus: extraction?.status,
                    validityStatus: validity?.status,
                  });
                  const viewableDocument = viewableDocuments.find(({ id }) => id === document.id);

                  if (isProcessing) {
                    return (
                      <DocumentProcessingStatus
                        onOpen={
                          viewableDocument ? () => selectDocument(viewableDocument) : undefined
                        }
                      />
                    );
                  }

                  if (extraction?.status === 'FAILED') {
                    return (
                      <ExtractionStatusBadge
                        status="FAILED"
                        errorMessage={extraction.errorMessage}
                        onRetry={() => handleRetryExtraction(document.id)}
                        isRetrying={isRetryingExtraction}
                      />
                    );
                  }

                  return null;
                }}
                renderBelowRow={document => {
                  const conformity = conformityByDocumentId?.get(document.id);
                  return conformity?.decision === 'REJECTED' ? (
                    <Text size="xs" tone="destructive" className="px-3 pb-2">
                      <Text inline weight="semibold">
                        {t('documentsListsPolicy.conformity.rejectedReasonPrefix')}
                      </Text>{' '}
                      {conformity.reason}
                    </Text>
                  ) : null;
                }}
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      <DocumentViewer
        productId={productId}
        document={selectedDocument}
        documents={viewableDocuments}
        onClose={closeViewer}
        onDocumentChange={selectDocument}
        documentCustomCategorieMappings={documentCustomCategorieMappings}
        uploadMode={uploadMode}
        extraction={selectedDocumentExtraction ?? null}
        extractionEnabled={extractionEnabled}
        onExtractionFeedback={extractionEnabled ? handleExtractionFeedback : undefined}
        isExtractionFeedbackPending={isFeedbackPending}
        validity={selectedDocumentValidity ?? null}
        validityEnabled={complianceEnabled}
        onValidityOverride={
          complianceEnabled && canOverrideValidity ? handleValidityOverride : undefined
        }
        isValidityOverriding={isValidityOverriding}
        coherence={selectedDocumentCoherence}
        coherenceEntity="brokerageFirm"
        conformity={selectedDocument ? conformityByDocumentId?.get(selectedDocument.id) : undefined}
        onAcceptConformity={onAcceptDocumentConformity}
        onRejectConformity={onRejectDocumentConformity}
        onCancelConformity={onCancelDocumentConformity}
      />
    </>
  );
}
